import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MobileFormPage } from '@/components/MobileFormPage';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { useCamadaActiva, useCrearCamada } from '@/lib/queries/camadas';
import { useAuthStore } from '@/stores/auth';
import type { Sexo } from '@/lib/types';
import { CODIGO_LOTE_MAX_LENGTH, CODIGO_LOTE_REGEX } from '@/lib/schemas/common';

export function CrearCamadaPage() {
  const user = useAuthStore((s) => s.user),
    owner = user?.id;
  const active = useCamadaActiva(),
    create = useCrearCamada(),
    toast = useToast();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [code, setCode] = useState(''),
    [sex, setSex] = useState<Sexo | ''>(''),
    [date, setDate] = useState(today);
  useEffect(() => {
    setCode('');
    setSex('');
    setDate(today);
  }, [owner, today]);
  const allowed = user?.rol === 'admin' || user?.rol === 'ganadero';
  if (active.data)
    return (
      <main className="cm-form-page mx-auto max-w-md px-4">
        <header className="ui-global-header">
          <AppHeaderContent backTo="/camadas" backLabel="Camadas" />
        </header>
        <h1 className="ui-page-title">Nueva camada</h1>
        <section className="cm-form-note">
          <h2>Ya hay una camada abierta</h2>
          <p>
            Camada {active.data.codigo_lote} está en curso. Solo puede haber una abierta en esta
            explotación.
          </p>
        </section>
        <Link className="ui-primary" to={`/camadas?camada=${active.data.id}`}>
          Ver camada actual
        </Link>
      </main>
    );
  async function save() {
    if (!allowed || active.data || active.isLoading || active.isError) return;
    if (!CODIGO_LOTE_REGEX.test(code.trim()))
      throw new Error(
        `El código debe tener entre 3 y ${CODIGO_LOTE_MAX_LENGTH} letras, números o guiones.`,
      );
    if (!sex) throw new Error('Selecciona el sexo de la camada.');
    if (!date) throw new Error('Selecciona la fecha de inicio.');
    const saved = await create.mutateAsync({
      codigo_lote: code.trim(),
      sexo: sex,
      fecha_entrada: date,
    });
    if (useAuthStore.getState().user?.id !== owner) return;
    toast.show({
      kind: 'success',
      message: 'Camada guardada.',
    });
    return `/camadas/${saved.id}/entradas-animales`;
  }
  return (
    <MobileFormPage
      title="Nueva camada"
      backTo="/camadas"
      backLabel="Camadas"
      saveLabel="Crear camada"
      dirty={!!code || !!sex || date !== today}
      disabled={!allowed || active.isLoading || active.isError}
      onSave={save}
    >
      {active.isLoading && <p role="status">Comprobando la camada actual…</p>}
      {active.isError && (
        <p role="alert">
          {extractApiMessage(active.error, 'Conecta para comprobar si hay una camada abierta.')}
        </p>
      )}
      {!allowed && (
        <p className="cm-form-note">
          Tu perfil permite consultar. Un ganadero o administrador debe crear la camada.
        </p>
      )}
      <div className="cm-form-field">
        <label htmlFor="camada-code">Código de camada</label>
        <input
          className="ui-field"
          id="camada-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={CODIGO_LOTE_MAX_LENGTH}
          placeholder="2026-01"
          autoComplete="off"
        />
        <p>Entre 3 y {CODIGO_LOTE_MAX_LENGTH} letras, números o guiones.</p>
      </div>
      <fieldset className="cm-sex">
        <legend>Sexo</legend>
        {(['machos', 'hembras'] as const).map((value) => (
          <label key={value}>
            <input
              type="radio"
              name="sexo"
              value={value}
              checked={sex === value}
              onChange={() => setSex(value)}
            />
            {value === 'machos' ? 'Machos' : 'Hembras'}
          </label>
        ))}
      </fieldset>
      <div className="cm-form-field">
        <label htmlFor="camada-date">Fecha de inicio</label>
        <input
          className="ui-field"
          id="camada-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>
      <p className="cm-form-note">Código y sexo quedan fijados al crear la camada. Fase de cebo.</p>
      <p className="cm-form-note">
        Después registrarás cada llegada de camión y prepararás las naves. Puedes hacerlo cuando
        lleguen los animales.
      </p>
    </MobileFormPage>
  );
}
export default CrearCamadaPage;
